import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { PutTreatmentBody } from '@snootsvet/shared'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

interface UpdateTreatmentParams {
  id: string
  patientId: string
  consultationId?: string
  body: PutTreatmentBody
}

/** Updates a treatment in-place via PUT, preserving the original prescriber. */
export function useUpdateTreatment() {
  const api = useOsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, patientId, consultationId, body }: UpdateTreatmentParams) => {
      const res = await api.put('/treatments/:id', { params: { id }, body })
      if (!res.success) throw new Error(res.error.message)
      return { treatment: res.data.treatment, patientId, consultationId }
    },
    onSuccess: ({ patientId, consultationId }) => {
      toast.success('Treatment updated')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['products'] })
      queryClient.invalidateQueries({ queryKey: ['product'] })
      queryClient.invalidateQueries({ queryKey: ['patient-orders', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patient-treatments', patientId] })
      queryClient.invalidateQueries({ queryKey: ['patient-timeline', patientId] })
      if (consultationId) {
        queryClient.invalidateQueries({ queryKey: ['consultation', consultationId] })
      }
      queryClient.invalidateQueries({ queryKey: ['tasks'] })
      queryClient.invalidateQueries({ queryKey: ['patient-tasks', patientId] })
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to update treatment')
    },
  })
}
